import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Dropdown, Menu, Table, Spin, Tooltip, Modal } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { useAppDispatch, useAppSelector, RootState } from "../../../../store";
import { svg } from "../../../../Assets/svg";
import FullScreenLoader from "../../../Components/FullScreenLoader";
import NoPermissionPage from "../../../Components/NoPermissionPage";
import CommonDeleteModal from "../../../Components/Modals/CommonDeleteModal";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import {
  GetBOMByIdAPI,
  DeleteBOMAPI,
  GetBOMPartsAndComponentsAPI,
  ExportBOMAPI,
  RegenerateBOMAPI,
} from "../../../Redux/stockProcessManager";
import {
  usePermissionsChecker,
  PagePermissionNameData,
} from "../../../../helpers/permissionModule";
import { useDebounce, formatToLocalDate } from "../../../../helpers/utils";
import { getDecryptedName } from "../../../../helpers/bycrypt";
import { STOCK_VARIABLES } from "../constants";
import {
  exportImportStatusClasess,
  exportImportStatusLabel,
  getProductImportActionLabel,
} from "../../Products/productsTypes";

interface ViewBOMParams {
  bomId?: string;
  isEmbedded: boolean;
  onDeleteSuccess?: () => void;
}

const ViewProductBOM: React.FC<ViewBOMParams> = ({
  bomId,
  isEmbedded = false,
  onDeleteSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { _id: paramsId } = useParams();

  const _id = bomId || paramsId;

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.PROCESS_MANAGER,
  );

  const { exportBOMLoading, authLoading } = useAppSelector(
    (state: RootState) => state?.loading,
  );

  const [data, setData] = useState<any>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [isRegenerateModal, setIsRegenerateModal] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  const [isPartsModalOpen, setIsPartsModalOpen] = useState<boolean>(false);

  // Parts & Components table state
  const [pcLayout, setPcLayout] = useState<any>(null);
  const [pcRows, setPcRows] = useState<any[]>([]);
  const [pcLoading, setPcLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [pcPage, setPcPage] = useState(1);
  const [hasMoreRows, setHasMoreRows] = useState(true);
  const [pcTotal, setPcTotal] = useState(0);
  const limit = 20;

  // ─── Load BOM meta ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (_id) {
      setInitialLoading(true);
      dispatch(
        GetBOMByIdAPI(_id, (res) => {
          if (res) {
            setData(res);
          } else {
            navigate(-1);
          }
          setInitialLoading(false);
        }),
      );
    }
  }, [_id, dispatch, navigate]);

  // ─── Load Parts & Components ───────────────────────────────────────────────
  const loadPartsAndComponents = useCallback(
    (pg: number, s: string, reset = false) => {
      if (!_id) return;
      setPcLoading(true);
      dispatch(
        GetBOMPartsAndComponentsAPI(
          { id: _id, page: pg, limit, search: s },
          (res) => {
            const incoming = res?.data || res;
            const newRows: any[] = incoming?.rows || [];
            const pag = incoming?.pagination;
            const layout = incoming?.layout;
            if (layout) setPcLayout(layout);
            if (pag) setPcTotal(pag.total);
            setPcRows((prev) =>
              reset || pg === 1 ? newRows : [...prev, ...newRows],
            );
            setHasMoreRows(pag ? pag.has_next : false);
            setPcLoading(false);
          },
          () => {
            setPcLoading(false);
            setHasMoreRows(false);
          },
        ),
      );
    },
    [_id, dispatch, limit],
  );

  useEffect(() => {
    setPcPage(1);
    setPcRows([]);
    setHasMoreRows(true);
    loadPartsAndComponents(1, debouncedSearch, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, debouncedSearch]);

  if (authLoading) return <FullScreenLoader />;
  if (!USER_PERMISSIONS?.READ) return <NoPermissionPage />;

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleEditClick = () =>
    navigate(`/process-manager/product-bom/create/${_id}`);
  const handleDeleteClick = () => setIsDeleteModal(true);
  const handleImportClick = () =>
    navigate(`/process-manager/product-bom/import/${_id}`);

  const handleExport = () => {
    if (!_id || exportBOMLoading) return;
    dispatch(
      ExportBOMAPI(_id, () => {
        dispatch(
          GetBOMByIdAPI(_id, (res) => {
            if (res) {
              setData(res);
            }
          }),
        );
      }),
    );
  };

  const handleDeleteBOM = () => {
    setDeleting(true);
    dispatch(
      DeleteBOMAPI({ id: _id }, () => {
        setIsDeleteModal(false);
        setDeleting(false);
        if (isEmbedded && onDeleteSuccess) {
          onDeleteSuccess();
        } else {
          navigate("/process-manager/product-bom");
        }
      }),
    );
  };

  const handleRegenerate = () => {
    if (!_id) return;
    setIsRegenerating(true);
    dispatch(
      RegenerateBOMAPI(
        _id,
        () => {
          dispatch(
            GetBOMByIdAPI(_id, (res) => {
              if (res) {
                setData(res);
              }
            }),
          );
          setIsRegenerating(false);
          setIsRegenerateModal(false);
        },
        () => {
          setIsRegenerating(false);
        },
      ),
    );
  };

  const handleSearch = (val: string) => {
    setSearch(val);
  };

  const searchBarClean = () => handleSearch("");

  const fetchNextPage = () => {
    const nextPage = pcPage + 1;
    setPcPage(nextPage);
    loadPartsAndComponents(nextPage, debouncedSearch);
  };

  // ─── Dropdown menu ─────────────────────────────────────────────────────────
  const MoreOptionsMenu = () => (
    <Menu>
      {USER_PERMISSIONS.MODIFY && (
        <Menu.Item key="1" onClick={handleEditClick}>
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Modify
          </div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.MODIFY && (
        <Menu.Item
          key="regenerate"
          onClick={() => {
            if (_id) {
              setIsRegenerateModal(true);
            }
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Regenerate
          </div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.ADD && (
        <Menu.Item key="2" onClick={handleImportClick}>
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Import
          </div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.READ && (
        <Menu.Item key="4" onClick={handleExport} disabled={exportBOMLoading}>
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            {exportBOMLoading ? "Exporting..." : "Export"}
          </div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.REMOVE && (
        <Menu.Item key="3" onClick={handleDeleteClick}>
          <div className="text-danger d-flex align-items-center gap-2 svg-20">
            Delete
          </div>
        </Menu.Item>
      )}
    </Menu>
  );

  const renderField = (
    label: string,
    value: any,
    className = "col-12 col-sm-6 col-md-4 col-lg-3",
  ) => (
    <div className={className}>
      <label htmlFor={label} className="custom-label mb-0 ff text-949494">
        {label}
      </label>
      <div id={label} className="mb-0 fs-6 fw-semibold d-block text-dark">
        {value !== undefined && value !== null && value !== "" ? value : "-"}
      </div>
    </div>
  );

  // ─── Build columns from pcLayout to perfectly match Google Sheet layout ───
  const layoutParts: any[] = pcLayout?.parts || [];

  const variantColumns = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      width: "10rem",
      render: (v: any) => (
        <span className="fw-bold text-dark">
          {v || data?.product_id?.title || data?.product_id?.name || "-"}
        </span>
      ),
    },
    {
      title: "Variant Title",
      dataIndex: "variant_name",
      key: "variant_name",
      width: "24rem",
      render: (v: any) => <span className="fw-bold text-dark">{v || "-"}</span>,
    },
    {
      title: "Product Code",
      dataIndex: "product_code",
      key: "product_code",
      render: (v: any) => v || "-",
    },
    // Dynamically nested Part groups
    ...layoutParts.map((lp: any, partIdx: number) => ({
      title: `Part (${lp.part_name || `Part ${lp.sequence || partIdx + 1}`})`,
      key: `part_${lp.part_id}`,
      children: [
        // Part Attributes & Variables
        ...(
          (lp.columns_config || [
            { key: "name", label: "Part Name" },
            ...STOCK_VARIABLES,
            { key: "qty", label: "Quantity" },
          ]) as any[]
        ).map((col: any) => ({
          title: col.label,
          key: `part_${lp.part_id}_${col.key}_col`,
          render: (_: any, record: any) => {
            const part = record.parts?.find(
              (p: any) => String(p.part_id) === String(lp.part_id),
            );
            if (part?.variables?.qty <= 0) {
              return "";
            }

            if (col.key === "name") {
              return part?.part_name || "-";
            }
            return part?.variables?.[col.key] ?? "-";
          },
        })),
        // Level 2: Components
        ...(lp.components || []).map((lc: any, compIdx: number) => ({
          title: `Component (${
            lc.component_name || `Component ${lc.sequence || compIdx + 1}`
          })`,
          key: `part_${lp.part_id}_comp_${lc.component_id}_group`,
          children: (
            (lc.columns_config || [
              { key: "name", label: "Component Name" },
              ...STOCK_VARIABLES,
              { key: "qty", label: "Quantity" },
            ]) as any[]
          ).map((col: any) => ({
            title: col.label,
            key: `part_${lp.part_id}_comp_${lc.component_id}_${col.key}_col`,
            render: (_: any, record: any) => {
              const part = record.parts?.find(
                (p: any) => String(p.part_id) === String(lp.part_id),
              );

              const comp = part?.components?.find(
                (c: any) => String(c.component_id) === String(lc.component_id),
              );
              if (comp?.variables?.qty <= 0) {
                return "";
              }
              console.log(part, "compcompcompcompcompcompcomp");
              if (col.key === "name") {
                return comp?.component_name || "-";
              }
              return comp?.variables?.[col.key] ?? "-";
            },
          })),
        })),
      ],
    })),
  ];

  return (
    <div
      className={`h-100 d-flex flex-column ${
        isEmbedded ? "" : "page-content px-40 py-40"
      }`}
    >
      {/* ── Header ── */}
      <div className="d-flex flex-shrink-0 flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center mb-md-4 mb-3">
        {isEmbedded ? (
          <h3 className="fw-bold mb-0 d-flex gap-2 align-items-center">
            BOM Details
          </h3>
        ) : (
          <h2 className="text-black fs-36 fw-bolder page-title mb-0">
            <span
              onClick={() => navigate(-1)}
              className="cursor-pointer svg-24 me-2"
            >
              {svg?.backArrow}
            </span>{" "}
            View Product BOM
          </h2>
        )}
        <div className="d-flex justify-content-end align-items-center gap-3">
          {/* More options */}
          {(USER_PERMISSIONS.MODIFY ||
            USER_PERMISSIONS.REMOVE ||
            USER_PERMISSIONS.ADD) && (
            <Dropdown
              overlay={MoreOptionsMenu}
              trigger={["click"]}
              placement="bottomRight"
              className="cursor-pointer"
              overlayClassName="moreOptionDropdown"
            >
              <span className="svg-32 text-white d-flex align-items-center justify-content-center">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          )}
        </div>
      </div>

      {/* ── Main Content Area ── */}
      {initialLoading ? (
        <FullScreenLoader />
      ) : data ? (
        <>
          {/* ── BOM Meta Info ── */}
          <div className="card flex-shrink-0 page-table-card border-0 shadow rounded-4 overflow-hidden mb-4">
            <div className="card-body p-md-4 p-3">
              <div className="d-flex flex-column gap-3 mt-2 px-1">
                <div className="row gy-3">
                  {renderField("BOM Name", data.name)}
                  {renderField(
                    "Product",
                    data.product_id?.title || data.product_id?.name,
                  )}
                  {renderField(
                    "Updated By",
                    getDecryptedName(data.updated_by || data.created_by),
                  )}
                  {renderField(
                    "Updated On",
                    formatToLocalDate(data.updated_at, true),
                  )}

                  <div className="col-12">
                    <div className="custom-label mb-1 ff text-949494">
                      Parts
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {data.parts?.length > 0 ? (
                        <>
                          {data.parts.slice(0, 3).map((p: any, idx: number) => (
                            <span
                              key={idx}
                              className="badge bg-light text-dark border px-2 py-1 fs-6 text-wrap text-start"
                            >
                              {p?.part_id?.title ||
                                p?.part_id?.name ||
                                p?.part_id}
                            </span>
                          ))}
                          {data.parts.length > 3 && (
                            <Tooltip title="View all parts">
                              <span
                                className="badge bg-light text-primary border px-2 py-1 fs-6 cursor-pointer"
                                onClick={() => setIsPartsModalOpen(true)}
                              >
                                +{data.parts.length - 3}
                              </span>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <span className="text-dark fw-semibold">-</span>
                      )}
                    </div>
                  </div>

                  {data.attachments?.length > 0 && (
                    <div className="col-12">
                      <div className="custom-label mb-1 ff text-949494">
                        Attachments
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {data.attachments.map((att: any, idx: number) => (
                          <a
                            key={idx}
                            href={att.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="badge bg-light text-primary border px-2 py-1 fs-6 text-decoration-none"
                          >
                            {att.file_name || `Attachment ${idx + 1}`}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Parts & Components Table Header ── */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-shrink-0 flex-wrap gap-2">
            <h3 className="fw-bold mb-0 d-flex gap-2 align-items-center">
              Product BOM Variants{" "}
              {data && pcTotal > 0 && <span>({pcTotal})</span>}{" "}
              {data?.export_process_batch ? (
                <span
                  className={
                    "d-inline-flex align-items-center billStatus " +
                    exportImportStatusClasess(
                      data?.export_process_batch?.status,
                    )
                  }
                >
                  Export{" "}
                  {exportImportStatusLabel(data?.export_process_batch?.status)}
                </span>
              ) : (
                ""
              )}
              {data?.import_process_batch ? (
                <span
                  className={
                    "d-inline-flex align-items-center billStatus " +
                    exportImportStatusClasess(
                      data?.import_process_batch?.status,
                    )
                  }
                >
                  {getProductImportActionLabel(data?.import_process_batch)}{" "}
                  {exportImportStatusLabel(data?.import_process_batch?.status)}
                </span>
              ) : (
                ""
              )}
            </h3>
            <div className="d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-sm-2 order-3">
              <div className="pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search variant or product code..."
                />
                {search?.length ? (
                  <span className="cursor-pointer" onClick={searchBarClean}>
                    {svg?.searchClear}
                  </span>
                ) : (
                  <span>{svg?.search}</span>
                )}
              </div>
            </div>
          </div>

          {/* ── Parts & Components Table ── */}
          <div className="card page-table-card variantsTableCard border-0 shadow rounded-4 variantsPartComponentTable overflow-hidden">
            <div className="card-body overflow-auto p-0" id="pcScrollableDiv">
              <InfiniteScroll
                dataLength={pcRows.length}
                next={fetchNextPage}
                hasMore={hasMoreRows}
                loader={
                  pcPage > 1 ? (
                    <div className="text-center p-2">
                      <Spin />
                    </div>
                  ) : null
                }
                scrollableTarget="pcScrollableDiv"
                style={{ height: "100%", overflow: "visible" }}
              >
                <Table
                  dataSource={pcRows}
                  columns={variantColumns}
                  loading={pcLoading && pcRows.length === 0}
                  rowKey={(r: any) => String(r.bom_variant_id || Math.random())}
                  scroll={{ x: "max-content" }}
                  bordered
                  size="small"
                  pagination={false}
                  locale={{
                    emptyText: pcLoading ? " " : "No variants found",
                  }}
                  className="variant-edit-table bg-white"
                />
              </InfiniteScroll>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-5 fs-5">No data found</div>
      )}

      {/* ── Delete Modal ── */}
      {isDeleteModal && (
        <CommonDeleteModal
          visible={isDeleteModal}
          setIsModalVisible={setIsDeleteModal}
          handleDelete={handleDeleteBOM}
          title={"Remove BOM, Are You Sure?"}
          description={
            "Are you sure you want to remove this BOM? All associated data will be permanently deleted."
          }
          setIsDeleteId={() => {}}
          isLoading={deleting}
          buttonName="REMOVE"
        />
      )}

      <CommonConfirmationModal
        open={isRegenerateModal}
        onClose={() => setIsRegenerateModal(false)}
        onSuccess={handleRegenerate}
        title={`Regenerate ${data?.name || ""}, Are You Sure?`}
        subTitle="All related Product BOM Variants will be updated."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Regenerate"
        loading={isRegenerating}
      />

      <Modal
        title="Parts"
        open={isPartsModalOpen}
        onCancel={() => setIsPartsModalOpen(false)}
        footer={null}
        centered
      >
        <ul className="mb-0 ps-3">
          {data?.parts?.map((p: any, idx: number) => (
            <li key={idx} className="mb-2 fs-6 text-dark">
              {p?.part_id?.title || p?.part_id?.name || p?.part_id}
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default ViewProductBOM;
